import { useState, useRef, useEffect, useMemo, type ComponentType } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Calculator,
  ChevronDown,
  Search,
  X,
  Sparkles,
  TrendingUp,
  HeartPulse,
  GraduationCap,
  Atom,
  ArrowRight,
  ExternalLink,
  Layers,
} from "lucide-react";
import { CALCULATORS, CALCULATOR_CATEGORIES } from "@/lib/calculators-catalog";
import { ES_CALCULATORS, ES_CALCULATOR_CATEGORIES } from "@/lib/es-calculators-catalog";
import { DE_CALCULATORS, DE_CALCULATOR_CATEGORIES } from "@/lib/de-calculators-catalog";
import { FR_CALCULATORS, FR_CALCULATOR_CATEGORIES } from "@/lib/fr-calculators-catalog";
import { PT_BR_CALCULATORS, PT_BR_CALCULATOR_CATEGORIES } from "@/lib/pt-br-calculators-catalog";
import type { Language } from "@/lib/i18n";
import { CalcIcon } from "@/components/calculators/CalcIcon";

export interface UniversalCalc {
  slug: string;
  name: string;
  category: string;
  icon: string;
  featured?: boolean;
  tagline?: string;
  keywords?: string[];
}

interface ColumnConfig {
  title: string;
  icon: ComponentType<{ className?: string }>;
  iconColor: string;
  categoryName: string;
  tools: UniversalCalc[];
}

interface MegaMenuTranslations {
  calculators: string;
  searchPlaceholder: string;
  allCalculators: string;
  allCategoriesLabel: string;
  viewAll: string;
  moreTools: string;
  searchResults: string;
  noResults: string;
  colMath: string;
  colFinance: string;
  colHealth: string;
  colEducation: string;
  colScience: string;
  exploreBannerTitle: string;
  exploreBannerSub: string;
}

const MEGA_MENU_I18N: Record<Language, MegaMenuTranslations> = {
  en: {
    calculators: "Calculators",
    searchPlaceholder: "Search 122+ free online calculators (e.g. percentage, bmi, gpa)...",
    allCalculators: "Browse All 122+ Calculators",
    allCategoriesLabel: "Browse by Category:",
    viewAll: "View all",
    moreTools: "calculators",
    searchResults: "Calculators matching",
    noResults: "No calculators found matching your search.",
    colMath: "Math & Algebra",
    colFinance: "Finance & Money",
    colHealth: "Health & Fitness",
    colEducation: "Grade & Education",
    colScience: "Science & Physics",
    exploreBannerTitle: "Free Browser Calculators",
    exploreBannerSub: "100% Client-Side · Private · Step-by-Step Formulas",
  },
  es: {
    calculators: "Calculadoras",
    searchPlaceholder: "Buscar entre 122+ calculadoras gratuitas (ej. porcentaje, imc, notas)...",
    allCalculators: "Ver las 122+ Calculadoras",
    allCategoriesLabel: "Por categoría:",
    viewAll: "Ver todas",
    moreTools: "calculadoras",
    searchResults: "Calculadoras para",
    noResults: "No se encontraron calculadoras con esa búsqueda.",
    colMath: "Matemáticas y Álgebra",
    colFinance: "Finanzas y Cotidiano",
    colHealth: "Salud y Fitness",
    colEducation: "Calificaciones y Educación",
    colScience: "Ciencia y Física",
    exploreBannerTitle: "Calculadoras Online Gratis",
    exploreBannerSub: "100% en el Navegador · Privacidad · Fórmulas",
  },
  de: {
    calculators: "Rechner",
    searchPlaceholder: "Über 122+ kostenlose Online-Rechner durchsuchen (z.B. BMI, Noten, Zinsen)...",
    allCalculators: "Alle 122+ Rechner ansehen",
    allCategoriesLabel: "Nach Kategorie:",
    viewAll: "Alle ansehen",
    moreTools: "Rechner",
    searchResults: "Rechner für",
    noResults: "Keine Rechner gefunden.",
    colMath: "Mathematik & Algebra",
    colFinance: "Finanzen & Alltag",
    colHealth: "Gesundheit & Fitness",
    colEducation: "Noten & Bildung",
    colScience: "Wissenschaft & Physik",
    exploreBannerTitle: "Kostenlose Online-Rechner",
    exploreBannerSub: "100% im Browser · Datenschutz · Schritt-für-Schritt",
  },
  fr: {
    calculators: "Calculateurs",
    searchPlaceholder: "Rechercher parmi 122+ calculateurs gratuits (ex. pourcentage, imc, gpa)...",
    allCalculators: "Voir les 122+ calculateurs",
    allCategoriesLabel: "Par catégorie :",
    viewAll: "Voir tout",
    moreTools: "calculateurs",
    searchResults: "Calculateurs pour",
    noResults: "Aucun calculateur trouvé.",
    colMath: "Mathématiques & Algèbre",
    colFinance: "Finance & Quotidien",
    colHealth: "Santé & Fitness",
    colEducation: "Notes & Éducation",
    colScience: "Science & Physique",
    exploreBannerTitle: "Calculateurs Gratuits en Ligne",
    exploreBannerSub: "100% Navigateur · Privé · Formules Étape par Étape",
  },
  "pt-BR": {
    calculators: "Calculadoras",
    searchPlaceholder: "Pesquisar entre 122+ calculadoras gratuitas (ex. porcentagem, imc, notas)...",
    allCalculators: "Ver todas as 122+ Calculadoras",
    allCategoriesLabel: "Por categoria:",
    viewAll: "Ver todas",
    moreTools: "calculadoras",
    searchResults: "Calculadoras para",
    noResults: "Nenhuma calculadora encontrada.",
    colMath: "Matemática & Álgebra",
    colFinance: "Finanças & Dia a Dia",
    colHealth: "Saúde & Boa Forma",
    colEducation: "Notas & Educação",
    colScience: "Ciência & Física",
    exploreBannerTitle: "Calculadoras Gratuitas Online",
    exploreBannerSub: "100% no Navegador · Privacidade · Fórmulas",
  },
};

export function getCalculatorsData(lang: Language) {
  switch (lang) {
    case "es":
      return {
        calculators: ES_CALCULATORS as UniversalCalc[],
        categories: ES_CALCULATOR_CATEGORIES,
        baseHref: "/es/calculators",
      };
    case "de":
      return {
        calculators: DE_CALCULATORS as UniversalCalc[],
        categories: DE_CALCULATOR_CATEGORIES,
        baseHref: "/de/calculators",
      };
    case "fr":
      return {
        calculators: FR_CALCULATORS as UniversalCalc[],
        categories: FR_CALCULATOR_CATEGORIES,
        baseHref: "/fr/calculators",
      };
    case "pt-BR":
      return {
        calculators: PT_BR_CALCULATORS as UniversalCalc[],
        categories: PT_BR_CALCULATOR_CATEGORIES,
        baseHref: "/pt-br/calculators",
      };
    default:
      return {
        calculators: CALCULATORS as UniversalCalc[],
        categories: CALCULATOR_CATEGORIES,
        baseHref: "/calculators",
      };
  }
}

/**
 * Build 5 curated columns mirroring FreeConvert's mega menu layout.
 */
function getColumns(
  calculators: UniversalCalc[],
  categories: string[],
  t: MegaMenuTranslations,
): ColumnConfig[] {
  // Safe helper to pick top featured items from a category
  const getTopFromCategories = (catNames: string[], limit = 8): UniversalCalc[] => {
    const matched = calculators.filter((c) => catNames.includes(c.category));
    // Sort featured first, then original order
    const featured = matched.filter((c) => c.featured);
    const nonFeatured = matched.filter((c) => !c.featured);
    return [...featured, ...nonFeatured].slice(0, limit);
  };

  const mathCat = categories[0] || "Math & Algebra";
  const educationCat = categories[1] || "Grade & Education";
  const financeCat = categories[2] || "Finance & Everyday";
  const healthCat = categories[3] || "Health & Fitness";
  const unitCat = categories[4] || "Unit & Physics";
  const conversionCat = categories[5] || "Conversion & Geometry";
  const engineeringCat = categories[7] || "Engineering & Electronics";
  const chemistryCat = categories[8] || "Chemistry & Science";

  return [
    {
      title: t.colMath,
      icon: Calculator,
      iconColor: "text-indigo-500 dark:text-indigo-400",
      categoryName: mathCat,
      tools: getTopFromCategories([mathCat], 8),
    },
    {
      title: t.colFinance,
      icon: TrendingUp,
      iconColor: "text-emerald-500 dark:text-emerald-400",
      categoryName: financeCat,
      tools: getTopFromCategories([financeCat], 8),
    },
    {
      title: t.colHealth,
      icon: HeartPulse,
      iconColor: "text-rose-500 dark:text-rose-400",
      categoryName: healthCat,
      tools: getTopFromCategories([healthCat], 8),
    },
    {
      title: t.colEducation,
      icon: GraduationCap,
      iconColor: "text-amber-500 dark:text-amber-400",
      categoryName: educationCat,
      tools: getTopFromCategories([educationCat], 8),
    },
    {
      title: t.colScience,
      icon: Atom,
      iconColor: "text-cyan-500 dark:text-cyan-400",
      categoryName: unitCat,
      tools: getTopFromCategories([unitCat, conversionCat, engineeringCat, chemistryCat], 8),
    },
  ];
}

interface CalculatorsMegaMenuProps {
  lang?: Language;
}

export function CalculatorsMegaMenu({ lang = "en" }: CalculatorsMegaMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const t = MEGA_MENU_I18N[lang] || MEGA_MENU_I18N.en;
  const { calculators, categories, baseHref } = useMemo(
    () => getCalculatorsData(lang),
    [lang],
  );

  const columns = useMemo(
    () => getColumns(calculators, categories, t),
    [calculators, categories, t],
  );

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setQuery("");
  }, [pathname]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Search filter
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return calculators
      .filter((c) => {
        return (
          c.name.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          (c.tagline && c.tagline.toLowerCase().includes(q)) ||
          (c.keywords && c.keywords.some((k) => k.toLowerCase().includes(q)))
        );
      })
      .slice(0, 16);
  }, [query, calculators]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative inline-block">
      {/* Desktop Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${t.calculators} Menu`}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
          isOpen
            ? "bg-secondary text-primary shadow-xs"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        }`}
      >
        <Calculator className="size-4 text-primary/90" />
        <span>{t.calculators}</span>
        <ChevronDown
          className={`size-3.5 text-muted-foreground/80 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-primary" : ""
          }`}
        />
      </button>

      {/* Floating Mega Menu Panel */}
      {isOpen && (
        <div
          ref={menuRef}
          role="region"
          aria-label="Calculators Directory"
          className="fixed left-1/2 -translate-x-1/2 top-14 mt-1.5 w-[min(1280px,calc(100vw-1.5rem))] z-50 rounded-2xl border border-border/80 bg-card/98 shadow-2xl backdrop-blur-2xl animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150 text-foreground overflow-hidden max-h-[calc(100vh-4.5rem)] flex flex-col"
        >
          {/* Header Bar: Quick Search + Stats */}
          <div className="border-b border-border/60 bg-muted/30 px-6 py-3.5 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full h-10 pl-10 pr-9 rounded-xl bg-background border border-border/80 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-2xs"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-md cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <Link
                to={baseHref}
                onClick={() => setIsOpen(false)}
                className="inline-flex items-center gap-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary px-3.5 py-2 text-xs sm:text-sm font-bold transition-colors"
              >
                <Sparkles className="size-4" />
                <span>{calculators.length}+ Free Tools</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </div>

          {/* Body Content */}
          <div className="overflow-y-auto flex-1 p-6 lg:p-7">
            {/* If user is typing in search, show Instant Search Results */}
            {query.trim().length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    {t.searchResults} &ldquo;{query}&rdquo; ({searchResults.length})
                  </p>
                  <Link
                    to={`${baseHref}?q=${encodeURIComponent(query)}`}
                    onClick={() => setIsOpen(false)}
                    className="text-sm font-bold text-primary hover:underline flex items-center gap-1.5"
                  >
                    View in directory <ArrowRight className="size-3.5" />
                  </Link>
                </div>

                {searchResults.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <p className="text-base font-semibold">{t.noResults}</p>
                    <p className="text-sm mt-1">
                      Try searching with other terms like &ldquo;gpa&rdquo;, &ldquo;percentage&rdquo;, or &ldquo;loan&rdquo;.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                    {searchResults.map((calc) => (
                      <Link
                        key={calc.slug}
                        to={`${baseHref}/${calc.slug}`}
                        onClick={() => setIsOpen(false)}
                        className="group flex flex-col justify-between p-3.5 rounded-xl border border-border/60 bg-background hover:border-primary/50 hover:bg-secondary/50 transition-all shadow-2xs"
                      >
                        <div className="flex items-start gap-3">
                          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <CalcIcon name={calc.icon} className="size-4.5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors truncate">
                              {calc.name}
                            </p>
                            <span className="inline-block mt-0.5 text-xs text-muted-foreground uppercase font-semibold">
                              {calc.category}
                            </span>
                          </div>
                        </div>
                        {calc.tagline && (
                          <p className="mt-2 text-xs text-muted-foreground line-clamp-1 leading-relaxed">
                            {calc.tagline}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Standard 5-Column Category Layout (FreeConvert Style with clear text) */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-0 lg:divide-x lg:divide-border/60">
                {columns.map((col, idx) => {
                  const IconCmp = col.icon;
                  return (
                    <div
                      key={col.title}
                      className={`flex flex-col justify-between ${
                        idx > 0 ? "lg:pl-6" : ""
                      } ${idx < columns.length - 1 ? "lg:pr-6" : ""}`}
                    >
                      <div>
                        {/* Column Category Header */}
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-2.5 pb-3 mb-3 border-b border-border/60 text-sm sm:text-[15px] font-bold tracking-tight text-foreground hover:text-primary transition-colors group"
                        >
                          <span className={`grid size-7 place-items-center rounded-lg bg-secondary/80 ${col.iconColor}`}>
                            <IconCmp className="size-4" />
                          </span>
                          <span className="truncate">{col.title}</span>
                          <ArrowRight className="size-3.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 ml-auto text-primary" />
                        </Link>

                        {/* List of Top Tools */}
                        <div className="space-y-1">
                          {col.tools.map((tool) => (
                            <Link
                              key={tool.slug}
                              to={`${baseHref}/${tool.slug}`}
                              onClick={() => setIsOpen(false)}
                              className="group flex items-center justify-between px-3 py-2 rounded-lg text-sm sm:text-[14px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                            >
                              <span className="truncate">{tool.name}</span>
                              {tool.featured && (
                                <span className="size-1.5 rounded-full bg-primary/70 shrink-0 ml-1.5" />
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* View All In Category Footer */}
                      <div className="pt-3.5 mt-2 border-t border-border/50">
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={() => setIsOpen(false)}
                          className="inline-flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-primary hover:underline"
                        >
                          <span>{t.viewAll} {col.title}</span>
                          <ArrowRight className="size-3" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Action Bar */}
          <div className="border-t border-border/70 bg-secondary/35 px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 text-sm">
            {/* Quick Category Chips */}
            <div className="flex flex-wrap items-center gap-2 max-w-3xl">
              <span className="text-xs sm:text-sm font-bold text-foreground mr-1 hidden sm:inline">
                {t.allCategoriesLabel}
              </span>
              {categories.slice(0, 7).map((cat) => (
                <Link
                  key={cat}
                  to={`${baseHref}?cat=${encodeURIComponent(cat)}`}
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg border border-border/70 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
                >
                  {cat}
                </Link>
              ))}
            </div>

            {/* Direct Link to Calculators Hub */}
            <Link
              to={baseHref}
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs sm:text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs ml-auto"
            >
              <Sparkles className="size-4" />
              <span>{t.allCalculators}</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Mobile Drawer Accordion Component.
 * Embedded inside the hamburger drawer in SiteHeader for seamless mobile responsiveness.
 */
interface CalculatorsMobileMenuProps {
  lang?: Language;
  onClose: () => void;
}

export function CalculatorsMobileMenu({ lang = "en", onClose }: CalculatorsMobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [mobileQuery, setMobileQuery] = useState("");

  const t = MEGA_MENU_I18N[lang] || MEGA_MENU_I18N.en;
  const { calculators, categories, baseHref } = useMemo(
    () => getCalculatorsData(lang),
    [lang],
  );

  const columns = useMemo(
    () => getColumns(calculators, categories, t),
    [calculators, categories, t],
  );

  const filtered = useMemo(() => {
    const q = mobileQuery.trim().toLowerCase();
    if (!q) return [];
    return calculators
      .filter((c) => c.name.toLowerCase().includes(q) || c.category.toLowerCase().includes(q))
      .slice(0, 10);
  }, [mobileQuery, calculators]);

  return (
    <div className="w-full">
      {/* Mobile Drawer Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-foreground transition-colors hover:bg-secondary cursor-pointer"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <span className="grid size-7 place-items-center rounded-lg bg-primary/10 text-primary">
            <Calculator className="size-4" />
          </span>
          <span>{t.calculators}</span>
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">
            {calculators.length}+
          </span>
        </div>
        <ChevronDown
          className={`size-4 text-muted-foreground transition-transform duration-200 ${
            isOpen ? "rotate-180 text-primary" : ""
          }`}
        />
      </button>

      {/* Expanded Accordion Content */}
      {isOpen && (
        <div className="mt-1.5 ml-2 pl-3 border-l-2 border-primary/30 space-y-3 py-2 animate-in slide-in-from-top-2 duration-150">
          {/* Quick Hub Link */}
          <Link
            to={baseHref}
            onClick={onClose}
            className="flex items-center justify-between rounded-xl bg-primary/10 p-3 text-xs sm:text-sm font-bold text-primary hover:bg-primary/20 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Sparkles className="size-4" />
              {t.allCalculators}
            </span>
            <ArrowRight className="size-4" />
          </Link>

          {/* Quick Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              value={mobileQuery}
              onChange={(e) => setMobileQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full h-9 pl-9 pr-8 rounded-lg bg-background border border-border/80 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {mobileQuery && (
              <button
                type="button"
                onClick={() => setMobileQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          {/* If search query is present */}
          {mobileQuery.trim().length > 0 ? (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {filtered.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`${baseHref}/${calc.slug}`}
                  onClick={onClose}
                  className="flex items-center justify-between p-2.5 rounded-lg text-xs sm:text-sm font-medium text-foreground bg-background hover:bg-secondary border border-border/60"
                >
                  <span className="truncate">{calc.name}</span>
                  <span className="text-[11px] text-muted-foreground ml-2 shrink-0">{calc.category}</span>
                </Link>
              ))}
              {filtered.length === 0 && (
                <p className="p-3 text-center text-xs text-muted-foreground">{t.noResults}</p>
              )}
            </div>
          ) : (
            /* Category Accordions on Mobile */
            <div className="space-y-2">
              {columns.map((col) => {
                const isCatOpen = activeCategory === col.title;
                const IconCmp = col.icon;
                return (
                  <div key={col.title} className="rounded-lg border border-border/50 bg-background/50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setActiveCategory(isCatOpen ? null : col.title)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-xs sm:text-sm font-bold text-foreground hover:bg-secondary/60 cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <IconCmp className={`size-4 ${col.iconColor}`} />
                        {col.title}
                      </span>
                      <ChevronDown
                        className={`size-3.5 text-muted-foreground transition-transform duration-200 ${
                          isCatOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isCatOpen && (
                      <div className="px-2.5 pb-2.5 pt-1 border-t border-border/40 space-y-1 bg-secondary/20">
                        {col.tools.slice(0, 6).map((tool) => (
                          <Link
                            key={tool.slug}
                            to={`${baseHref}/${tool.slug}`}
                            onClick={onClose}
                            className="block px-2.5 py-2 rounded-md text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors truncate"
                          >
                            {tool.name}
                          </Link>
                        ))}
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={onClose}
                          className="inline-flex items-center gap-1 px-2.5 pt-1.5 text-xs font-bold text-primary hover:underline"
                        >
                          {t.viewAll} {col.title} →
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
